import { createContext, useContext, useState, ReactNode, useEffect } from 'react';
import { getApiUrl } from '@/utils/apiConfig';

const API_URL = getApiUrl();

export type Role = 'CITIZEN' | 'PARTY' | 'ONG' | 'INSTITUTION' | 'ADMIN' | 'MEDIA';

export interface User {
  id: string;
  name: string;
  email: string;
  role: Role;
  partyAcronym?: string;
  phone?: string;
  
  // Nouveaux champs pour Hiérarchie Admin
  adminLevel?: 'SUPER_ADMIN' | 'RESTRICTED_ADMIN';
  adminCategories?: string[];
  
  // Nouveaux champs pour Hiérarchie Délégué Parti
  isPartyDelegate?: boolean;
  requestedDelegateStatus?: boolean;
  accreditationStatus?: 'none' | 'pending' | 'approved' | 'rejected' | 'blocked';
  isPrincipalDelegate?: boolean;
  secondaryStatus?: 'none' | 'pending' | 'approved' | 'rejected';
  secondaryRequestReason?: string;

  // Champs spécifiques Parti, ONG & Media
  qualite?: string;
  // Champs spécifiques Média
  channelName?: string;
  mediaType?: string;
  mediaTitleFr?: string;
  mediaTitleAr?: string;
  mediaTitleZgh?: string;
  // Champs spécifiques ONG
  ongName?: string;
  champsActivite?: string;
  // Champs spécifiques Institution
  institutionName?: string;
  qualiteInstitution?: string;
  // Champs spécifiques Citoyen (Espace Normal)
  profession?: string; // Gardé pour rétrocompatibilité, remplacé par categorieSociopro ci-dessous
  categorieSociopro?: string;
  genre?: string;
  age?: string;
  residence?: string;
  affinitePolitique?: string;
  isAssociationMember?: boolean;
  associativeName?: string;
  expectations?: string;

  // Champs "metadata" pour le profil Partenaire
  description?: string;
  statut?: string;
  activites?: string;
  contribution?: string;
  projets?: string;
  website?: string;
  facebook?: string;
  twitter?: string;
  instagram?: string;
  youtube?: string;
  logoUrl?: string;
  image1Url?: string;
  image2Url?: string;
  ongLevel?: string;
  ongSiege?: string;
  
  // Nouveaux champs pour structures parallèles du parti
  jeunesseUrl?: string;
  femmesUrl?: string;
  academieUrl?: string;
  partyPrinciples?: { title: string; description: string; readMoreUrl?: string }[];
  
  metadata?: any; // Conteneur optionnel pour envoyer l'update au backend

  // Lock / validation fields for Partner profile
  modificationsCount?: number;
  adminAuthorization?: boolean;
  metadataStatus?: 'none' | 'pending' | 'approved' | 'rejected' | 'en_attente' | 'rejete';
  pendingMetadata?: any;
  metadataFeedback?: string;
  unlockRequestStatus?: 'none' | 'pending' | 'approved' | 'rejected';
  unlockRequestFeedback?: string;
  createdAt?: string;
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  login: (email: string, password?: string) => Promise<void>;
  loginAdmin: (email: string, password?: string) => Promise<void>;
  register: (userData: any) => Promise<void>;
  updateUser: (updates: Partial<User>) => Promise<boolean>;
  forgotPassword: (email: string) => Promise<void>;
  resetPassword: (token: string, newPassword: string) => Promise<void>;
  checkAuth: () => Promise<void>;
  logout: () => void;
  isAuthenticated: boolean;
  isParty: boolean;
  isPartyDelegate: boolean;
  isOng: boolean;
  isAdmin: boolean;
  isSuperAdmin: boolean;
  isRestrictedAdmin: boolean;
  isInstitution: boolean;
  isMedia: boolean;
  isPrincipalDelegate: boolean;
  hasAccessToDashboard: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(() => {
    if (typeof window !== "undefined") {
      const storedUser = localStorage.getItem('kaynin_user');
      if (storedUser) {
        try {
          return JSON.parse(storedUser);
        } catch (e) {
          console.error("Erreur de parsing utilisateur local");
          return null;
        }
      }
    }
    return null;
  });

  const [token, setToken] = useState<string | null>(() => {
    if (typeof window !== "undefined") {
      return localStorage.getItem('kaynin_auth_token');
    }
    return null;
  });

  useEffect(() => {
    // Keep this just in case other tabs modify local storage
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'kaynin_user') {
        if (e.newValue) {
          try {
            setUser(JSON.parse(e.newValue));
          } catch (e) {}
        } else {
          setUser(null);
        }
      }
    };
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  const checkAuth = async () => {
    if (!token) return;
    try {
      const res = await fetch(`${API_URL}/auth.php?action=me`, {
        method: "GET"
        // Authorization header is auto-injected by apiConfig.ts interceptor
      });
      if (res.ok) {
        const data = await res.json();
        if (data.status === "success" && data.user) {
          setUser(data.user);
          localStorage.setItem('kaynin_user', JSON.stringify(data.user));
        }
      }
    } catch (e) {
      console.error("Failed to check auth:", e);
    }
  };

  useEffect(() => {
    if (token) {
      checkAuth();
    }
  }, [token]);

  const login = async (email: string, password?: string) => {
    try {
      const res = await fetch(`${API_URL}/auth.php?action=login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password: password || "password" })
      });
      if (!res.ok) throw new Error("Erreur de connexion");
      const data = await res.json();
      
      // BLOQUER les administrateurs depuis la connexion publique
      if (data.user && (data.user.role === 'ADMIN' || data.user.admin_level === 'SUPER_ADMIN')) {
        throw new Error("Accès administrateur refusé via le portail public. Utilisez kaynin.ma/in");
      }

      setUser(data.user);
      localStorage.setItem('kaynin_user', JSON.stringify(data.user));
      if (data.token) {
        localStorage.setItem('kaynin_auth_token', data.token);
      }
    } catch (e) {
      console.error(e);
      throw e;
    }
  };

  const loginAdmin = async (email: string, password?: string) => {
    try {
      const res = await fetch(`${API_URL}/auth.php?action=login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password: password || "password", source: "admin_portal" })
      });
      if (!res.ok) throw new Error("Erreur de connexion");
      const data = await res.json();
      
      // EXIGER le rôle administrateur
      if (!data.user || (data.user.role !== 'ADMIN' && data.user.admin_level !== 'SUPER_ADMIN')) {
        throw new Error("Privilèges insuffisants. Ce portail est réservé aux administrateurs.");
      }

      setUser(data.user);
      localStorage.setItem('kaynin_user', JSON.stringify(data.user));
      if (data.token) {
        localStorage.setItem('kaynin_auth_token', data.token);
      }
    } catch (e) {
      console.error(e);
      throw e;
    }
  };

  const register = async (userData: any) => {
    try {
      const res = await fetch(`${API_URL}/auth.php?action=register`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...userData, password: userData.password || "password" })
      });
      if (!res.ok) {
         const err = await res.json();
         throw new Error(err.error || "Erreur d'inscription");
      }
      const data = await res.json();
      setUser(data.user);
      localStorage.setItem('kaynin_user', JSON.stringify(data.user));
      if (data.token) {
        localStorage.setItem('kaynin_auth_token', data.token);
      }
    } catch (e) {
      console.error(e);
      throw e;
    }
  };

  const updateUser = async (updates: Partial<User>): Promise<boolean> => {
    if (user) {
      try {
        const encodedPayload = btoa(unescape(encodeURIComponent(JSON.stringify({ id: user.id, ...updates }))));
        
        const res = await fetch(`${API_URL}/auth.php?action=update`, {
          method: "POST",
          headers: { "Content-Type": "text/plain" },
          body: encodedPayload
        });
        
        if (!res.ok) {
           let errText = await res.text();
           try {
             const errJson = JSON.parse(errText);
             if (errJson.error) errText = errJson.error;
           } catch(e) {}
           console.error("Backend Error on updateUser:", res.status, errText);
           alert("Erreur de synchronisation avec le serveur: " + errText);
           return false;
        }
        
        const updatedUser = { ...user, ...updates };
        // Si on passe un objet metadata (pour le backend), on fusionne aussi ses clés au niveau racine du state pour le frontend
        if (updates.metadata) {
          Object.assign(updatedUser, updates.metadata);
          updatedUser.modificationsCount = (updatedUser.modificationsCount || 0) + 1;
          updatedUser.metadataStatus = 'en_attente';
          updatedUser.adminAuthorization = false;
        }
        setUser(updatedUser);
        localStorage.setItem('kaynin_user', JSON.stringify(updatedUser));
        return true;
      } catch (e) {
        console.error("Failed to update user on server", e);
        alert("Erreur de connexion au serveur.");
        return false;
      }
    }
    return false;
  };

  const forgotPassword = async (email: string) => {
    try {
      const res = await fetch(`${API_URL}/auth.php?action=forgot_password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email })
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error || "Erreur lors de la demande");
      }
    } catch (e) {
      console.error(e);
      throw e;
    }
  };

  const resetPassword = async (token: string, newPassword: string) => {
    try {
      const res = await fetch(`${API_URL}/auth.php?action=reset_password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, new_password: newPassword })
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error || "Erreur lors de la réinitialisation");
      }
    } catch (e) {
      console.error(e);
      throw e;
    }
  };

  const logout = () => {
    setUser(null);
    setToken(null);
    localStorage.removeItem('kaynin_user');
    localStorage.removeItem('kaynin_auth_token');
  };

  return (
    <AuthContext.Provider value={{
      user,
      token,
      login,
      loginAdmin,
      register,
      updateUser,
      forgotPassword,
      resetPassword,
      checkAuth,
      logout,
      isAuthenticated: !!user,
      isParty: user?.role === 'PARTY',
      isPartyDelegate: !!(user?.role === 'PARTY' && user?.isPartyDelegate),
      isOng: user?.role === 'ONG',
      isAdmin: user?.role === 'ADMIN',
      isSuperAdmin: user?.role === 'ADMIN' && (user?.adminLevel === 'SUPER_ADMIN' || !user?.adminLevel),
      isRestrictedAdmin: user?.role === 'ADMIN' && user?.adminLevel === 'RESTRICTED_ADMIN',
      isInstitution: user?.role === 'INSTITUTION',
      isMedia: user?.role === 'MEDIA',
      isPrincipalDelegate: !!user?.isPrincipalDelegate,
      hasAccessToDashboard: !!user && (
        user.role === 'ADMIN' ||
        ((user.role === 'PARTY' || user.role === 'ONG' || user.role === 'MEDIA' || user.role === 'INSTITUTION') && user.accreditationStatus === 'approved')
      )
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
